import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  Activity, LayoutDashboard, Users, CalendarDays, Stethoscope, Receipt, FileText,
  ScrollText, Settings, Search, Moon, Sun, LogOut, Bell, Building2, Trash2, BarChart3,
  FlaskConical, BedDouble, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "doctor", "nurse", "receptionist"] },
  { to: "/patients", label: "Patients", icon: Users, roles: ["super_admin", "doctor", "nurse", "receptionist"] },
  { to: "/appointments", label: "Appointments", icon: CalendarDays, roles: ["super_admin", "doctor", "nurse", "receptionist"] },
  { to: "/consultations", label: "Consultations", icon: Stethoscope, roles: ["super_admin", "doctor", "nurse"] },
  { to: "/investigations", label: "Investigations", icon: FlaskConical, roles: ["super_admin", "doctor", "nurse"] },
  { to: "/admissions", label: "Admissions", icon: BedDouble, roles: ["super_admin", "doctor", "nurse"] },
  { to: "/certificates", label: "Certificates", icon: BadgeCheck, roles: ["super_admin", "doctor"] },
  { to: "/prescriptions", label: "Prescriptions", icon: FileText, roles: ["super_admin", "doctor", "nurse", "receptionist"] },
  { to: "/billing", label: "Billing", icon: Receipt, roles: ["super_admin", "doctor", "receptionist"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["super_admin", "doctor"] },
  { to: "/audit", label: "Audit logs", icon: ScrollText, roles: ["super_admin"] },
  { to: "/recycle-bin", label: "Recycle bin", icon: Trash2, roles: ["super_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
];

export default function Shell() {
  const { user, logout, theme, setTheme, facilities, facilityId, selectFacility } = useApp();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ patients: [], consultations: [], prescriptions: [] });
  const [notes, setNotes] = useState([]);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkConnection = async () => {
      if (!navigator.onLine) {
        if (!cancelled) setIsOnline(false);
        return;
      }

      try {
        await api.get("/license/status", { timeout: 10000 });
        if (!cancelled) setIsOnline(true);
      } catch {
        if (!cancelled) setIsOnline(false);
      }
    };

    checkConnection();

    const handleOnline = () => checkConnection();
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const interval = setInterval(checkConnection, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);


  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotes(data)).catch(() => {});
  }, [loc.pathname]);

  const search = async (q) => {
    if (q.length < 2) return setResults({ patients: [], consultations: [], prescriptions: [] });
    const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
    setResults(data);
  };

  const items = NAV.filter((n) => n.roles.includes(user.role));
  const active = facilities.find((f) => String(f.id) === String(facilityId));

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex flex-col">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
          <div className="h-8 w-8 rounded-md bg-primary/10 grid place-items-center">
            <Activity className="h-4.5 w-4.5 text-primary" />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-semibold text-sm tracking-tight">Zuniva Care</p>
            <p className={`text-[10px] font-mono flex items-center gap-1.5 ${
              isOnline ? "text-emerald-600" : "text-red-500"
            }`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${
                isOnline ? "bg-emerald-500" : "bg-red-500"
              }`} />
              {isOnline ? "ONLINE" : "OFFLINE"} · v1.0
            </p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {items.map(({ to, label, icon: Icon }) => {
            const on = loc.pathname === to;
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  on ? "bg-primary text-primary-foreground font-medium"
                     : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                <Icon className="h-4 w-4" />{label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <p className="text-xs font-medium truncate">{user.full_name}</p>
          <p className="text-[11px] text-muted-foreground font-mono uppercase">{user.role.replace("_", " ")}</p>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-border bg-card/70 backdrop-blur flex items-center gap-3 px-4 sticky top-0 z-20">
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="global-search-button"
            className="gap-2 text-muted-foreground font-normal w-full max-w-xs justify-start">
            <Search className="h-3.5 w-3.5" /> Search patients, Rx…
            <kbd className="ml-auto font-mono text-[10px] border border-border rounded px-1">⌘K</kbd>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="facility-switcher">
                <Building2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline max-w-[180px] truncate">{active ? active.name : "All facilities"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel>Facility scope</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => selectFacility("all")} data-testid="facility-option-all">
                Combined (all facilities)
              </DropdownMenuItem>
              {facilities.map((f) => (
                <DropdownMenuItem key={f.id} onClick={() => selectFacility(String(f.id))}
                  data-testid={`facility-option-${f.id}`}>
                  <span className="truncate">{f.name}</span>
                  <Badge variant="secondary" className="ml-auto text-[10px]">{f.type}</Badge>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notifications-button" className="relative">
                  <Bell className="h-4 w-4" />
                  {notes.length > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notes.length === 0 && <p className="p-3 text-sm text-muted-foreground">Nothing pending.</p>}
                {notes.map((n, i) => (
                  <div key={i} className="px-3 py-2 text-sm" data-testid={`notification-${i}`}>
                    <p className="font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.message}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" data-testid="logout-button"
              onClick={async () => { await logout(); nav("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 p-6 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name, UHID, phone, diagnosis or Rx number…"
            onValueChange={search} data-testid="global-search-input" />
          <CommandList>
            <CommandEmpty>No records found.</CommandEmpty>
            {results.patients.length > 0 && (
              <CommandGroup heading="Patients">
                {results.patients.map((r) => (
                  <CommandItem key={r.id} onSelect={() => { setOpen(false); nav(`/patients/${r.id}`); }}>
                    {r.label} <span className="ml-2 text-xs text-muted-foreground">{r.sub}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.consultations.length > 0 && (
              <CommandGroup heading="Diagnoses">
                {results.consultations.map((r) => (
                  <CommandItem key={r.id} onSelect={() => { setOpen(false); nav(`/consultations`); }}>
                    {r.label} <span className="ml-2 text-xs text-muted-foreground">{r.sub}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.prescriptions.length > 0 && (
              <CommandGroup heading="Prescriptions">
                {results.prescriptions.map((r) => (
                  <CommandItem key={r.id} onSelect={() => { setOpen(false); nav("/prescriptions"); }}>
                    {r.label} <span className="ml-2 text-xs text-muted-foreground">{r.sub}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </div>
  );
}
